import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CloudDownload as CloudDownloadIcon,
  CloudDone as CloudDoneIcon,
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { logout } from '../store/authSlice';
import { scaricaDatiGiornata, getGiornataSalvata } from '../utils/offlineGiornata';
import { ROLE_LABELS } from './Login';

const Impostazioni = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [online, setOnline] = useState(navigator.onLine);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ pct: 0, msg: '' });
  const [snapshot, setSnapshot] = useState(getGiornataSalvata());
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const roles = user?.roles && user.roles.length ? user.roles : [user?.role].filter(Boolean);

  const handleScarica = async () => {
    setMsg(null);
    setDownloading(true);
    setProgress({ pct: 0, msg: 'Avvio…' });
    try {
      const snap = await scaricaDatiGiornata(null, (m, pct) => setProgress({ pct, msg: m }));
      setSnapshot(snap);
      setMsg({ type: 'success', text: `Dati di oggi pronti per l'uso offline (${snap.count} interventi).` });
    } catch (err) {
      setMsg({ type: 'error', text: 'Errore durante lo scaricamento dei dati. Riprova quando sei online.' });
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <Box sx={{ pb: { xs: 10, sm: 4 } }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #3a0000 100%)',
          color: 'white',
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 4 },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <SettingsIcon sx={{ fontSize: 44 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Impostazioni</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>Dati offline, account e informazioni app</Typography>
          </Box>
        </Stack>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3, px: { xs: 2, sm: 3 } }}>
        {msg && (
          <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        )}

        {/* DATI OFFLINE */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudDownloadIcon color="primary" /> Dati per l'offline
            </Typography>
            <Chip
              icon={online ? <WifiIcon /> : <WifiOffIcon />}
              label={online ? 'Online' : 'Offline'}
              color={online ? 'success' : 'warning'}
              size="small"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scarica in anticipo tutti i dati della giornata (interventi, clienti, cantieri, presenze,
            timbrature, task e mezzi) così potrai lavorare anche senza connessione in cantiere.
          </Typography>

          {downloading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={progress.pct} sx={{ borderRadius: 1, height: 8 }} />
              <Typography variant="caption" color="text.secondary">{progress.msg}</Typography>
            </Box>
          )}

          {snapshot && !downloading && (
            <Alert icon={<CloudDoneIcon />} severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Ultimo scaricamento: <strong>{dayjs(snapshot.scaricato_il).format('DD/MM/YYYY HH:mm')}</strong>
              {' · '}giornata {dayjs(snapshot.data).format('DD/MM/YYYY')} · {snapshot.count} interventi
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={<CloudDownloadIcon />}
            onClick={handleScarica}
            disabled={downloading || !online}
            fullWidth
          >
            {downloading ? 'Scaricamento in corso…' : 'Scarica dati di oggi'}
          </Button>
          {!online && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Sei offline: connettiti per scaricare i dati.
            </Typography>
          )}
        </Paper>

        {/* ACCOUNT */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Account</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="body1" sx={{ mr: 1 }}>{user?.nome} {user?.cognome}</Typography>
            {roles.map((r) => (
              <Chip key={r} label={ROLE_LABELS[r] || r} size="small" color="primary" variant="outlined" />
            ))}
          </Stack>
          <List disablePadding>
            <ListItemButton onClick={() => navigate('/profilo')} sx={{ borderRadius: 1 }}>
              <ListItemIcon><PersonIcon /></ListItemIcon>
              <ListItemText primary="Il mio profilo" secondary="Modifica dati e password" />
            </ListItemButton>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1, color: 'error.main' }}>
              <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
              <ListItemText primary="Esci" />
            </ListItemButton>
          </List>
        </Paper>

        {/* INFO APP */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Informazioni</Typography>
          <List disablePadding>
            <ListItem disableGutters>
              <ListItemIcon><InfoIcon color="action" /></ListItemIcon>
              <ListItemText primary="NC Elettrica · Gestione Cantieri" secondary="Applicazione web installabile (PWA)" />
            </ListItem>
          </List>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Sviluppato da Spinotto Web Agency
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Impostazioni;
